import * as yup from 'yup';
import { useContext, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular, Info20Filled, Warning20Filled } from '@fluentui/react-icons';
import { ButtonLoading, ControlledField, IconLabel } from '@shared/components';
import { SignInContext, WorkflowPhase, useUsers } from '@modules/users';
import evalApprovalRules from '../helpers/evalApprovalRules.helper';
import resolveApprovalWorkflow from '../helpers/resolveApprovalWorkflow.helper';
import { useCreateApproval } from '../api';
import { ApprovalStatus, APPROVAL_PHASE_KEYS, TApprovalPhaseKey, TViewApprovalDialogState } from '../types';
import { TDocument } from '@modules/documents';
import { TFile } from '@modules/files';
import {
  ITEM_MIMETYPES,
  TItemType,
  findItemVersion,
  isFileItemVersion,
  isTDraft,
  getCustomFieldValues,
} from '@modules/items';
import config from '@config/index';
import CustomRuleErrors from './CustomRuleErrors';
import ApprovalPhaseUserField from './ApprovalPhaseUserField';
import InvalidApprovalFunctionWarning from './InvalidApprovalFunctionWarning';
import {
  resolveInvalidFunctionNames,
  rowsFromApprovalUsers,
  rowsFromUserIds,
  rowsFromInitialApprovalUsers,
  serializeApprovalUserEntries,
} from '../helpers/functionRows.helper';
import getInitialApprovalUsers from '../helpers/getInitialApprovalUsers.helper';
import useFunctionRows from '../hooks/useFunctionRows';
import { MAX_AUTHOR_FUNCTIONS_FOR_PREFILL } from '../constants';

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  master: TDocument | TFile;
  itemType: TItemType;
  versionId?: string;
  setViewApprovalDialogState: (state?: TViewApprovalDialogState) => void;
};

const phaseArrayField = (phaseKey: TApprovalPhaseKey, label: string) =>
  yup
    .array()
    .of(yup.string().required())
    .when('$enabledPhases', {
      is: (phases: TApprovalPhaseKey[]) => phases.includes(phaseKey),
      then: (s) => s.required(`Select at least one ${label}.`).min(1, `At least one ${label} is required.`),
      otherwise: (s) => s.optional().default([]),
    });

const schema = yup.object({
  authors: phaseArrayField(ApprovalStatus.AUTHORING, 'author'),
  reviewers: phaseArrayField(ApprovalStatus.REVIEWING, 'reviewer'),
  approvers: phaseArrayField(ApprovalStatus.APPROVING, 'approver'),
  violationNote: yup
    .string()
    .when(['$enabledPhases', '$initialAuthors', 'authors'], ([enabledPhases, initialAuthors, authors], s) => {
      const hasAuthoring = (enabledPhases as TApprovalPhaseKey[]).includes(ApprovalStatus.AUTHORING);
      if (!hasAuthoring) return s.notRequired();
      const missing = (initialAuthors as string[]).some((id) => !(authors as string[]).includes(id));
      return missing ? s.required('Reason for guideline violation is required.') : s.notRequired();
    }),
  file: yup
    .mixed<FileList>()
    .nullable()
    .when('$showFileUpload', {
      is: true,
      then: (s) =>
        s
          .required('File is required.')
          .test(
            'size',
            `File exceeds our file size limit of ${config.MAX_FILE_SIZES_MB.IMPORT_FILE}MB!`,
            (value) => value && value[0].size <= config.MAX_FILE_SIZES_MB.IMPORT_FILE * 1024 * 1024
          ),
    }),
});
type TFormData = yup.InferType<typeof schema>;

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '820px',
  },
  dialogContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
  },
});

const CreateApprovalDialog = ({
  isOpen,
  setIsOpen,
  master,
  itemType,
  versionId,
  setViewApprovalDialogState,
}: TProps) => {
  const { organization } = useContext(SignInContext);
  const { userMap } = useUsers();
  const isApprovalFunctionsEnabled = organization.featureSettings.approvalFunctions?.enabled;
  const importedInitialApprovalUsers = getInitialApprovalUsers(master);

  // --- Compute initial authors based on versionId and master.versions ---
  const fallbackInitialAuthors = useMemo(() => {
    if (!versionId) return [];
    const newAuthorIds = new Set<string>();
    let foundVersion = false;
    for (const versionIter of master.versions.slice().reverse()) {
      foundVersion = foundVersion || versionIter.id === versionId;
      if (!foundVersion) continue;
      if (
        master.approvals.find(
          (approval) => approval.versionId === versionIter.id && approval.status === ApprovalStatus.APPROVED
        )
      )
        break;
      versionIter.authors.forEach((authorId: string) => newAuthorIds.add(authorId));
    }
    return Array.from(newAuthorIds);
  }, [master.approvals, master.versions, versionId]);

  const itemVersion = findItemVersion(master, versionId);
  const version = itemVersion?.version;
  const mimetype = isFileItemVersion(itemVersion) && !isTDraft(itemVersion.version) && itemVersion.version.mimetype;
  const showFileUpload = mimetype ? !ITEM_MIMETYPES.pdf.includes(mimetype) : false;

  // --- Resolve workflow and enabled phases ---
  const customFieldValues = useMemo(() => getCustomFieldValues(version), [version]);
  const workflow = useMemo(
    () => resolveApprovalWorkflow(organization.approvalWorkflows, customFieldValues),
    [organization.approvalWorkflows, customFieldValues]
  );
  const enabledPhases: TApprovalPhaseKey[] = useMemo(() => {
    if (!workflow?.enabledPhases) return [...APPROVAL_PHASE_KEYS];
    return APPROVAL_PHASE_KEYS.filter((key) => workflow.enabledPhases?.[key]);
  }, [workflow]);
  const hasAuthoring = enabledPhases.includes(ApprovalStatus.AUTHORING);
  const hasReviewing = enabledPhases.includes(ApprovalStatus.REVIEWING);
  const hasApproving = enabledPhases.includes(ApprovalStatus.APPROVING);

  // --- Pre-fill reviewers/approvers ---
  let initialReviewers: string[] = [];
  let initialApprovers: string[] = [];
  const lastApproval = master.approvals.length > 0 ? master.approvals[master.approvals.length - 1] : undefined;
  const importedAuthors = importedInitialApprovalUsers?.AUTHORING || [];
  const importedReviewers = importedInitialApprovalUsers?.REVIEWING || [];
  const importedApprovers = importedInitialApprovalUsers?.APPROVING || [];
  const importedAuthorIds = Array.from(new Set(importedAuthors.map((entry) => entry.user).filter(Boolean))) as string[];
  const importedReviewerIds = Array.from(
    new Set(importedReviewers.map((entry) => entry.user).filter(Boolean))
  ) as string[];
  const importedApproverIds = Array.from(
    new Set(importedApprovers.map((entry) => entry.user).filter(Boolean))
  ) as string[];

  const initialAuthors = lastApproval
    ? fallbackInitialAuthors
    : importedAuthorIds.length > 0
      ? importedAuthorIds
      : fallbackInitialAuthors;
  if (lastApproval) {
    initialReviewers = lastApproval.phases.REVIEWING?.users?.map((u) => u.user) ?? [];
    initialApprovers = lastApproval.phases.APPROVING?.users?.map((u) => u.user) ?? [];
  } else {
    initialReviewers = importedReviewerIds;
    initialApprovers = importedApproverIds;
  }

  // --- Function rows (only used when feature ON) ---
  const activeGroupIds = useMemo(
    () => new Set(organization.customGroups.map((g) => g.id)),
    [organization.customGroups]
  );
  const initialAuthorRows = useMemo(() => {
    if (!lastApproval && importedAuthors.length > 0) return rowsFromInitialApprovalUsers(importedAuthors);
    if (!userMap) return [];
    return rowsFromUserIds(initialAuthors, userMap, activeGroupIds, MAX_AUTHOR_FUNCTIONS_FOR_PREFILL);
  }, [initialAuthors, importedAuthors, lastApproval, userMap, activeGroupIds]);
  const initialReviewerRows = useMemo(
    () =>
      lastApproval?.phases.REVIEWING
        ? rowsFromApprovalUsers(lastApproval.phases.REVIEWING.users)
        : importedReviewers.length > 0
          ? rowsFromInitialApprovalUsers(importedReviewers)
          : [],
    [importedReviewers, lastApproval]
  );
  const initialApproverRows = useMemo(
    () =>
      lastApproval?.phases.APPROVING
        ? rowsFromApprovalUsers(lastApproval.phases.APPROVING.users)
        : importedApprovers.length > 0
          ? rowsFromInitialApprovalUsers(importedApprovers)
          : [],
    [importedApprovers, lastApproval]
  );

  const defaultValues: TFormData = {
    authors: initialAuthors,
    reviewers: initialReviewers,
    approvers: initialApprovers,
    violationNote: '',
    file: null,
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<TFormData>({
    resolver: yupResolver(schema),
    defaultValues,
    context: { initialAuthors, showFileUpload, enabledPhases },
  });

  const [authorRows, setAuthorRows] = useFunctionRows(initialAuthorRows, (ids) =>
    setValue('authors', ids, { shouldValidate: true })
  );
  const [reviewerRows, setReviewerRows] = useFunctionRows(initialReviewerRows, (ids) =>
    setValue('reviewers', ids, { shouldValidate: true })
  );
  const [approverRows, setApproverRows] = useFunctionRows(initialApproverRows, (ids) =>
    setValue('approvers', ids, { shouldValidate: true })
  );

  // Authors the prefill left unassigned (over the group limit) and the user has not assigned yet.
  const skippedAuthorNames = useMemo(() => {
    if (!isApprovalFunctionsEnabled || !userMap || (!lastApproval && importedAuthors.length > 0)) return [];
    return initialAuthors
      .filter((id) => !initialAuthorRows.some((row) => row.userIds.includes(id)))
      .filter((id) => !authorRows.some((row) => row.userIds.includes(id)))
      .map((id) => userMap[id]?.name || 'Unknown');
  }, [
    initialAuthors,
    initialAuthorRows,
    authorRows,
    importedAuthors,
    lastApproval,
    userMap,
    isApprovalFunctionsEnabled,
  ]);

  const invalidFunctionNames = useMemo(() => {
    if (!isApprovalFunctionsEnabled || !userMap) return [];
    const submittedPhases = [
      ...(hasAuthoring ? [{ rows: authorRows }] : []),
      ...(hasReviewing ? [{ rows: reviewerRows }] : []),
      ...(hasApproving ? [{ rows: approverRows }] : []),
    ];
    return resolveInvalidFunctionNames(submittedPhases, activeGroupIds, userMap);
  }, [
    isApprovalFunctionsEnabled,
    userMap,
    activeGroupIds,
    hasAuthoring,
    hasReviewing,
    hasApproving,
    authorRows,
    reviewerRows,
    approverRows,
  ]);

  // Reset on open - when feature is ON, rows drive the form user IDs (fixes desync)
  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      if (isApprovalFunctionsEnabled) {
        setAuthorRows(initialAuthorRows);
        setReviewerRows(initialReviewerRows);
        setApproverRows(initialApproverRows);
      }
    }
  }, [isOpen]);

  // Watch selections
  const selectedAuthors = watch('authors') ?? [];
  const selectedReviewers = watch('reviewers') ?? [];
  const selectedApprovers = watch('approvers') ?? [];
  const hasMissingAuthor = useMemo(
    () => hasAuthoring && initialAuthors.some((id) => !selectedAuthors.includes(id)),
    [hasAuthoring, initialAuthors, selectedAuthors]
  );

  // --- custom rules evaluation ---
  const rules = workflow ? { minimumUsers: workflow.minimumUsers, uniqueUsers: workflow.uniqueUsers } : undefined;
  const ruleErrors = useMemo(
    () =>
      evalApprovalRules(
        rules,
        {
          [WorkflowPhase.AUTHORING]: hasAuthoring ? selectedAuthors : [],
          [WorkflowPhase.REVIEWING]: hasReviewing ? selectedReviewers : [],
          [WorkflowPhase.APPROVING]: hasApproving ? selectedApprovers : [],
        },
        customFieldValues
      ),
    [
      rules,
      hasAuthoring,
      hasReviewing,
      hasApproving,
      selectedAuthors,
      selectedReviewers,
      selectedApprovers,
      customFieldValues,
    ]
  );

  // API hook
  const createApproval = useCreateApproval();
  const onSubmit = (data: TFormData) => {
    if (!versionId) return;
    const jsFile = data.file?.[0];
    createApproval.mutate(
      {
        id: master._id,
        itemType,
        versionId,
        jsFile,
        violationNote: data.violationNote,
        ...(hasAuthoring && {
          authors: serializeApprovalUserEntries(isApprovalFunctionsEnabled, authorRows, data.authors),
        }),
        ...(hasReviewing && {
          reviewers: serializeApprovalUserEntries(isApprovalFunctionsEnabled, reviewerRows, data.reviewers),
        }),
        ...(hasApproving && {
          approvers: serializeApprovalUserEntries(isApprovalFunctionsEnabled, approverRows, data.approvers),
        }),
      },
      {
        onSuccess: ({ approval }) => {
          setIsOpen(false);
          setViewApprovalDialogState({ isOpen: true, approvalId: approval._id });
        },
      }
    );
  };

  // increase version messaging
  let increasedVersion = '?';
  let increaseVersionMessage = '';
  if (master.stats.latestVersionId !== versionId)
    increaseVersionMessage = 'The version number will not be increased because it is not the latest version.';
  else if (versionId) {
    const parts = versionId.split('.').map((n) => parseInt(n));
    if (parts[1] === 0)
      increaseVersionMessage = `The version number will not be increased because ${versionId} is already a major version.`;
    else {
      increasedVersion = parts.map((num, i) => (i === 0 ? num + 1 : 0)).join('.');
      increaseVersionMessage = `The version number will be increased to ${increasedVersion}.`;
    }
  }

  const classes = useStyles();

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
      <DialogSurface className={isApprovalFunctionsEnabled ? classes.dialogSurface : ''}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              Create approval request for version {versionId}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <div className="flex flex-col space-y-3 mb-4 mt-2">
                {hasAuthoring && (
                  <ApprovalPhaseUserField
                    phase={ApprovalStatus.AUTHORING}
                    functionsEnabled={isApprovalFunctionsEnabled}
                    control={control}
                    errors={errors}
                    rows={authorRows}
                    onRowsChange={setAuthorRows}
                    customGroups={organization.customGroups}
                    required
                  />
                )}

                {hasAuthoring && skippedAuthorNames.length > 0 && (
                  <IconLabel icon={<Warning20Filled />}>
                    No function pre-selected (in more than {MAX_AUTHOR_FUNCTIONS_FOR_PREFILL} functions):{' '}
                    {skippedAuthorNames.join(', ')}. Select one manually.
                  </IconLabel>
                )}

                <InvalidApprovalFunctionWarning userNames={invalidFunctionNames} />

                {hasMissingAuthor && (
                  <ControlledField
                    name="violationNote"
                    control={control}
                    errors={errors}
                    label="Reason for guideline violation"
                    placeholder="Justify why not all authors of this version are included."
                    inputType="textarea"
                    required
                  />
                )}

                {hasReviewing && (
                  <ApprovalPhaseUserField
                    phase={ApprovalStatus.REVIEWING}
                    functionsEnabled={isApprovalFunctionsEnabled}
                    control={control}
                    errors={errors}
                    rows={reviewerRows}
                    onRowsChange={setReviewerRows}
                    customGroups={organization.customGroups}
                    required
                  />
                )}
                {hasApproving && (
                  <ApprovalPhaseUserField
                    phase={ApprovalStatus.APPROVING}
                    functionsEnabled={isApprovalFunctionsEnabled}
                    control={control}
                    errors={errors}
                    rows={approverRows}
                    onRowsChange={setApproverRows}
                    customGroups={organization.customGroups}
                    required
                  />
                )}

                {showFileUpload && (
                  <ControlledField
                    inputType="file"
                    label="Upload PDF file"
                    name="file"
                    accept={ITEM_MIMETYPES.pdf.join(',')}
                    multiple={false}
                    control={control}
                    errors={errors}
                    required
                  >
                    Select file
                  </ControlledField>
                )}

                <CustomRuleErrors ruleErrors={ruleErrors} />

                {increaseVersionMessage && (
                  <p className="flex items-center space-x-1">
                    <Info20Filled />
                    <span>{increaseVersionMessage}</span>
                  </p>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <ButtonLoading
                type="submit"
                appearance="primary"
                loading={createApproval.isPending}
                disabled={!versionId || !isValid || ruleErrors.length > 0 || invalidFunctionNames.length > 0}
              >
                Create
              </ButtonLoading>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default CreateApprovalDialog;
